// Centralised translations for HMMM.
// Keys are dot-prefixed by area. Where a string only appears once we keep the
// English text very close to the variable name for grep-ability.

export const LANGUAGES = [
  { code: "en", label: "EN", full: "English" },
  { code: "id", label: "ID", full: "Bahasa Indonesia" },
];

export const TRANSLATIONS = {
  en: {
    // Brand & footer
    "brand.subtitle": "Handy Medical Math Machine",
    "sidebar.lang_label": "Language",
    "sidebar.footer.version": "v0.1 · For professional use.",
    "sidebar.footer.disclaimer": "Always verify with local protocols and pharmacist.",

    // Nav
    "nav.iv_fluid": "IV Fluid Maintenance",
    "nav.iv_fluid.sub": "Holliday-Segar",
    "nav.pediatric": "Pediatric Dosing",
    "nav.pediatric.sub": "Weight-based mg/kg",
    "nav.drip": "IV Drip Rate",
    "nav.drip.sub": "Gtt / min",
    "nav.electrolytes": "Electrolytes",
    "nav.electrolytes.sub": "Na & K correction",
    "nav.nihss": "NIHSS",
    "nav.nihss.sub": "Stroke scale",
    "nav.alvarado": "Alvarado Score",
    "nav.alvarado.sub": "Appendicitis",
    "nav.bmi": "BMI",
    "nav.bmi.sub": "Body Mass Index",
    "nav.obgyn": "OB-Gyn",
    "nav.obgyn.sub": "EDD & Gestational Age",
    "nav.group.critical": "Emergency & Critical Care",
    "nav.group.scores": "Scores & Specialty",
    "nav.group.neonatology": "Neonatology",
    "nav.group.others": "Others",
    "nav.emergency": "Emergency Drugs",
    "nav.emergency.sub": "ACLS infusions",
    "nav.heparin": "Heparin Protocol",
    "nav.heparin.sub": "Bolus & maintenance",
    "nav.dev_prefix": "in development · ",

    // Top-right widget
    "widget.label": "Feedback & Support",
    "widget.title": "Help shape HMMM",
    "widget.subtitle": "Send feedback or support development.",
    "widget.feedback.title": "Feedback",
    "widget.feedback.body":
      "Report a bug, request a calculator or share a clinical note.",
    "widget.feedback.cta": "Open Google Form",
    "widget.donate.title": "Donate",
    "widget.donate.body":
      "Scan the QR with your camera to support development.",
    "widget.thanks": "Thank you for keeping this open & free.",

    // Changelog
    "changelog.kicker": "Changelog",
    "changelog.title": "What's new",
    "changelog.current": "Current",
    "gemini.title": "Ask Gemini",
    "gemini.body": "AI-assisted clinical look-ups are coming in v2.1. We're polishing prompt safety and reference sources before letting it loose at the bedside.",
    "gemini.placeholder": "Ask a clinical question — e.g. ‘paracetamol dose 16 kg child’ or ‘NIHSS interpretation 12’.",
    "gemini.ask": "Ask",
    "gemini.thinking": "Thinking…",
    "gemini.disclaimer":
      "AI output may be inaccurate. Always verify with your local protocols, pharmacist and the patient's renal / hepatic status.",

    // Theme
    "theme.label": "Theme",
    "theme.light": "Light",
    "theme.dark": "Dark",

    // Generic
    "common.calculator": "Calculator",
    "common.section": "Section",
    "common.reset": "Reset",
    "common.reset_all": "Reset all items",
    "common.dev_badge": "Dev",
    "common.in_development": "in development",
    "common.in_development.long": "In development.",
    "common.no_data_stored": "No data stored",
    "footer.tagline": "Free • Lightweight • No Login Required • Ad-Free • 100% Private",
    "footer.privacy":
      "🔒 Privacy Guaranteed: This application does not collect, store, or record your patient's clinical data or identity. All calculations run locally on your device.",

    // Home
    "home.kicker": "HMMM · Handy Medical Math Machine",
    "home.headline.1": "The math, done.",
    "home.headline.2": "So you can do the medicine.",
    "home.lead":
      "A handy bedside companion for the ER and the ward — ten no-nonsense calculators that update as you type and show every step of the math. No login, no tracking, no waiting.",
    "home.calculators": "Calculators",
    "home.disclaimer.label": "Disclaimer.",
    "home.disclaimer.body":
      "This tool assists, it does not decide. Verify every dose against the most recent local formulary and the patient's renal/hepatic function before administration.",
    "home.card.iv_fluid.sub": "Holliday-Segar (4-2-1 rule)",
    "home.card.iv_fluid.desc":
      "Maintenance hourly and 24h fluid requirement based on body weight.",
    "home.card.pediatric.sub": "Weight-based mg/kg",
    "home.card.pediatric.desc":
      "Common ER drugs (paracetamol, amoxicillin, ceftriaxone, dexamethasone, adrenaline, …).",
    "home.card.drip.sub": "Gtt / min from volume & time",
    "home.card.drip.desc":
      "Drops per minute for gravity sets (10, 15, 20, 60 gtt/ml).",
    "home.card.electrolytes.sub": "Na deficit & K replacement",
    "home.card.electrolytes.desc":
      "Sodium deficit, free water deficit, and potassium replacement estimates.",
    "home.card.nihss.sub": "Stroke scale (0–42)",
    "home.card.nihss.desc":
      "15-item NIH Stroke Scale with severity stratification.",
    "home.card.alvarado.sub": "Appendicitis (MANTRELS)",
    "home.card.alvarado.desc":
      "Probability of acute appendicitis from symptoms, signs and labs.",

    // Home tabs (v2.1)
    "tabs.algorithm": "Medical Algorithm",
    "tabs.drug": "Drug & Fluid",
    "tabs.guidelines": "Guidelines",
    "tabs.scores": "Clinical Scores",
    "scores.intro":
      "Standardised bedside scoring tools — quickly triage severity, risk-stratify, and decide escalation.",
    "score.killip.title": "Killip Class",
    "score.killip.desc": "Post-MI 30-day mortality risk stratification.",
    "score.sofa.title": "SOFA / qSOFA",
    "score.sofa.desc": "Sepsis screening (qSOFA) + organ dysfunction score (SOFA).",
    "score.news.title": "NEWS2",
    "score.news.desc": "National Early Warning Score — 7 vital-sign points.",
    "score.apgar.title": "Apgar Score",
    "score.apgar.desc": "Neonatal 5-criteria assessment at 1 and 5 minutes.",
    "score.ballard.title": "New Ballard Score",
    "score.ballard.desc": "Gestational age from 12-criterion physical & neuromuscular exam.",
    "score.downe.title": "Downe Score",
    "score.downe.desc": "Neonatal respiratory distress screening — 5 criteria, PONED thresholds.",
    "home.collab.prefix": "In collaboration with ",
    "home.collab.suffix": " for medical algorithms and guidelines.",
    "home.collab2.prefix": "In collaboration with ",
    "home.collab2.suffix": " for series of medical cases and e-books. Check their account!",
    "drug.intro":
      "Direct shortcuts to the drug-dose and fluid calculators already available in this app.",
    "guidelines.intro":
      "Curated summaries of ACLS, ATLS, WHO IMCI, and local formulary references are landing in v2.2. In the meantime every calculator page cites the source formula in its formula box.",
    "common.coming_v22": "Coming in v2.2",

    // Medical Algorithm — specialty index (on Home tab)
    "algo.intro":
      "Disease and emergency algorithms, grouped by specialty. Select a section to browse the available pathways.",
    "algo.count": "{n} algorithms available",
    "algo.spec.cardiovascular": "Cardiovascular",
    "algo.spec.respiratory": "Respiratory",
    "algo.spec.neurology": "Neurology",
    "algo.spec.pediatric": "Pediatric",
    "algo.spec.anestesi": "Anesthesia & Airway",
    "algo.footer_note":
      "Interactive flowcharts for the remaining specialties ship in v2.2. Cardiovascular pathways (ACLS, STEMI) are live now.",

    // Algorithm pages (Cardiovascular index + flowchart)
    "algo.cardiovascular.title": "Cardiovascular algorithms",
    "algo.cardiovascular.description":
      "Interactive step-by-step pathways for the most time-critical cardiovascular emergencies.",
    "algo.list.intro": "Algorithms in this section. Click any that are marked \"Ready\" to open.",
    "algo.badge.ready": "Ready",
    "algo.badge.soon": "Coming soon",
    "algo.back_home": "Back to Home",
    "algo.back_to_list": "Back to Cardiovascular",
    "algo.back_to_specialty": "Back to {name}",
    "algo.step_of": "Step {i} of {n}",
    "algo.previous": "Previous",
    "algo.restart": "Restart",
    "algo.disclaimer.label": "Reference only.",
    "algo.disclaimer.body":
      "These flowcharts summarise published guidelines and are provided for bedside memory aid. Always follow local protocols and verify doses with your pharmacist.",

    // IV Fluid
    "iv.title": "IV Fluid Maintenance",
    "iv.subtitle":
      "Holliday-Segar (4-2-1) method for daily and hourly maintenance fluids in pediatric and adult patients.",
    "iv.formula":
      "4 ml/kg/hr (first 10 kg) + 2 ml/kg/hr (next 10 kg) + 1 ml/kg/hr (each kg > 20)",
    "iv.weight": "Patient weight",
    "iv.weight.placeholder": "e.g. 18",
    "iv.note":
      "Use actual body weight in non-obese patients. For obese adults consider using ideal body weight or capping fluid rates per local policy.",
    "iv.result.rate": "Maintenance rate",
    "iv.result.day": "24-hour total",
    "iv.breakdown.empty": "Enter a weight to see the calculation breakdown.",

    // Pediatric
    "ped.title": "Pediatric Drug Dosing",
    "ped.subtitle":
      "Body-weight based (mg/kg) dosing for common ER medications. Max single and daily doses are applied automatically.",
    "ped.formula":
      "Dose (mg) = weight (kg) × mg/kg     —     capped at max single dose",
    "ped.medication": "Medication",
    "ped.weight": "Patient weight",
    "ped.weight.placeholder": "e.g. 14",
    "ped.custom": "Dose per kg (default {default} mg/kg)",
    "ped.custom.hint": "Range: {min}–{max} mg/kg · Frequency: {freq}",
    "ped.dev_suffix": " · (in development)",
    "ped.capped":
      "Capped to max single dose. Calculated dose ({raw} mg) exceeds the maximum ({max} mg).",
    "ped.dev_alert":
      "Dose values for this drug have not yet been clinically reviewed. Verify against your local formulary before use.",
    "ped.result.single": "Dose per administration",
    "ped.result.volume": "Volume to draw",
    "ped.result.frequency": "Frequency",
    "ped.result.daily_max": "Daily maximum",
    "ped.breakdown.empty": "Select medication, enter weight.",
    "ped.breakdown.route": "Route",

    // Drip
    "drip.title": "IV Drip Rate",
    "drip.subtitle":
      "Gravity infusion calculator. Computes drops per minute and ml per hour from total volume, duration, and the drop factor of your IV set.",
    "drip.formula": "gtt/min = (volume × drop factor) ÷ time (min)",
    "drip.volume": "Total volume",
    "drip.volume.placeholder": "e.g. 1000",
    "drip.hours": "Hours",
    "drip.hours.placeholder": "e.g. 8",
    "drip.minutes": "Minutes",
    "drip.minutes.placeholder": "0",
    "drip.factor": "Drop factor (IV set)",
    "drip.factor.macro10": "Macro · 10 gtt/ml",
    "drip.factor.macro15": "Macro · 15 gtt/ml",
    "drip.factor.macro20": "Macro · 20 gtt/ml",
    "drip.factor.micro60": "Micro · 60 gtt/ml",
    "drip.note":
      "Macroset drop factors are printed on the IV chamber. Microsets (60 gtt/ml) are used for precise low-volume infusions, especially in pediatrics.",
    "drip.result.gtt": "Drip rate",
    "drip.result.ml": "Flow rate",
    "drip.breakdown.empty": "Enter volume, time and drop factor.",

    // Electrolytes
    "elec.title": "Electrolyte Correction",
    "elec.subtitle":
      "Sodium deficit / free water deficit and potassium replacement estimates. Always correct slowly and re-check labs.",
    "elec.formula":
      "Na deficit (mEq) = TBW × (desired Na − measured Na)   ·   FWD (L) = TBW × (Na/140 − 1)",
    "elec.dev_banner":
      "Formulas (TBW, Na deficit, FWD, empirical K deficit) are not yet clinically reviewed. Use as an estimate only and verify with your local protocol before prescribing.",
    "elec.weight": "Patient weight",
    "elec.weight.placeholder": "e.g. 70",
    "elec.tbw": "TBW estimate",
    "elec.tbw.male": "Adult male — 0.6 × kg",
    "elec.tbw.female": "Adult female — 0.5 × kg",
    "elec.tbw.geriatric_male": "Geriatric male — 0.5 × kg",
    "elec.tbw.geriatric_female": "Geriatric female — 0.45 × kg",
    "elec.tbw.child": "Child — 0.6 × kg",
    "elec.tbw.infant": "Infant — 0.7 × kg",
    "elec.sodium": "Sodium correction",
    "elec.na.measured": "Measured Na",
    "elec.na.desired": "Desired Na",
    "elec.na.note":
      "Correct chronic hyponatremia by a maximum of 8–10 mEq/L per 24 hours to prevent Osmotic Demyelination Syndrome (ODS). Consider entering a Desired Na of [Measured Na + 8] as a safe guide for the first 24 hours.",
    "elec.result.na_deficit": "Sodium deficit",
    "elec.result.fwd": "Free water deficit",
    "elec.result.tbw": "Total body water",
    "elec.result.correction_time": "Min. correction time (at 8 mEq/L/24h)",
    "elec.breakdown.empty": "Enter weight and sodium values.",
    "elec.potassium": "Potassium replacement",
    "elec.k.measured": "Measured K⁺",
    "elec.k.note":
      "Correction > 10 mEq/hour (via central line) MUST be accompanied by continuous ECG monitoring.",
    "elec.result.k_deficit": "Estimated K deficit",
    "elec.result.k_peripheral": "Infusion time (peripheral · 10 mEq/h)",
    "elec.result.k_central": "Infusion time (central · 20 mEq/h)",
    "elec.k.breakdown.empty": "Enter weight and potassium values.",

    // NIHSS
    "nihss.title": "NIHSS — Stroke Scale",
    "nihss.subtitle":
      "National Institutes of Health Stroke Scale. 15 items, 0–42 points. Document at presentation, post-tPA, and at 24 h.",
    "nihss.formula": "Total = sum of all items (0 to 42)",
    "nihss.result.total": "Total NIHSS",
    "nihss.result.unit": "/ 42 · {answered}/{total} answered",
    "nihss.result.severity": "Severity",
    "nihss.severity.none": "No stroke symptoms",
    "nihss.severity.minor": "Minor stroke",
    "nihss.severity.moderate": "Moderate stroke",
    "nihss.severity.moderate_severe": "Moderate–severe stroke",
    "nihss.severity.severe": "Severe stroke",
    "nihss.legend":
      "0           No stroke symptoms\n1–4        Minor stroke\n5–15       Moderate stroke\n16–20      Moderate–severe stroke\n21–42      Severe stroke",
    "nihss.note":
      "Use total NIHSS together with clinical judgement, time-of-onset, and imaging when considering thrombolysis or thrombectomy.",

    // Alvarado
    "alv.title": "Alvarado Score — Appendicitis",
    "alv.subtitle":
      "MANTRELS criteria for suspected acute appendicitis in adults. Score 0–10 stratifies probability and guides imaging vs. surgical referral.",
    "alv.formula":
      "Σ points from 8 criteria (max 10). RLQ tenderness and leukocytosis weighted ×2.",
    "alv.group.symptoms": "Symptoms",
    "alv.group.signs": "Signs",
    "alv.group.labs": "Labs",
    "alv.result.total": "Total score",
    "alv.result.interpretation": "Interpretation",
    "alv.interp.low": "Think of other possibilities",
    "alv.interp.possible": "Possible appendicitis — observe / imaging",
    "alv.interp.probable": "Probable appendicitis — surgical review",
    "alv.interp.very":
      "Very probable appendicitis — urgent surgical referral",
    "alv.legend":
      "0–3   Think of other possibilities  — discharge likely safe\n4–6   Possible            — observe / imaging (CT, US)\n7–8   Probable            — surgical referral\n9–10  Very probable       — urgent surgical referral",
    "alv.note":
      "Alvarado is less reliable in children, pregnancy, and elderly patients. Combine with clinical assessment and imaging where indicated.",

    // BMI
    "bmi.title": "Body Mass Index (BMI)",
    "bmi.subtitle":
      "Adult BMI with two reference scales — international WHO and Asia-Pacific (WHO 2004 / IDF). Switch between them to see the category change.",
    "bmi.formula": "BMI = weight (kg) ÷ height (m)²",
    "bmi.weight": "Weight",
    "bmi.weight.placeholder": "e.g. 65",
    "bmi.height": "Height",
    "bmi.height.placeholder": "e.g. 170",
    "bmi.criteria": "Reference scale",
    "bmi.criteria.international": "International (WHO)",
    "bmi.criteria.asia_pacific": "Asia-Pacific (WHO 2004)",
    "bmi.result.bmi": "BMI",
    "bmi.result.category": "Category",
    "bmi.cat.underweight": "Underweight",
    "bmi.cat.normal": "Normal",
    "bmi.cat.overweight": "Overweight",
    "bmi.cat.at_risk": "Overweight (at risk)",
    "bmi.cat.obese1": "Obese class I",
    "bmi.cat.obese2": "Obese class II",
    "bmi.cat.obese3": "Obese class III",
    "bmi.legend.international":
      "< 18.5    Underweight\n18.5–24.9 Normal\n25.0–29.9 Overweight\n30.0–34.9 Obese class I\n35.0–39.9 Obese class II\n≥ 40.0    Obese class III",
    "bmi.legend.asia_pacific":
      "< 18.5    Underweight\n18.5–22.9 Normal\n23.0–24.9 Overweight (at risk)\n25.0–29.9 Obese class I\n≥ 30.0    Obese class II",
    "bmi.note":
      "BMI does not distinguish muscle from fat. In athletes, pregnancy and oedema, interpret with caution. Asia-Pacific cut-offs (lower thresholds) are widely used for South-East Asian populations.",
    "bmi.empty": "Enter weight and height.",

    // OB-Gyn
    "obgyn.title": "OB-Gyn — EDD & Gestational Age",
    "obgyn.subtitle":
      "Estimate Expected Date of Delivery (Naegele's rule) and current Gestational Age from the patient's last menstrual period (LMP).",
    "obgyn.formula":
      "EDD = LMP + 7 days − 3 months + 1 year   ·   GA = today − LMP",
    "obgyn.lmp": "Last Menstrual Period (LMP)",
    "obgyn.lmp.hint": "First day of the last normal menstrual period.",
    "obgyn.today": "Today",
    "obgyn.result.edd": "Estimated Date of Delivery",
    "obgyn.result.ga": "Gestational Age",
    "obgyn.result.ga.unit": "weeks · days",
    "obgyn.result.days_remaining": "Days remaining",
    "obgyn.result.days_remaining.unit": "days",
    "obgyn.result.trimester": "Trimester",
    "obgyn.trimester.1": "First (≤ 13⁺⁶)",
    "obgyn.trimester.2": "Second (14⁰ – 27⁺⁶)",
    "obgyn.trimester.3": "Third (≥ 28⁰)",
    "obgyn.result.past_due": "Past due date",
    "obgyn.weeks": "weeks",
    "obgyn.days": "days",
    "obgyn.note":
      "Naegele's rule assumes a regular 28-day cycle. For irregular cycles or after IVF, prefer ultrasound dating in the first trimester (CRL).",
    "obgyn.empty": "Select an LMP date.",
    "obgyn.future_lmp": "LMP cannot be in the future.",
    "obgyn.too_far":
      "LMP appears more than 45 weeks ago — please verify the date.",
    "obgyn.today.hint":
      "Defaults to today. Backdate to cross-check earlier ANC records.",

    // Johnson-Toshack
    "johnson.title": "Estimated Fetal Weight (Johnson-Toshack)",
    "johnson.subtitle":
      "Bedside fetal weight estimate from fundal height and station of the presenting part.",
    "johnson.tfu": "Fundal height (TFU)",
    "johnson.station": "Station of presenting part",
    "johnson.station.high": "Symphysis · not engaged (m = 12)",
    "johnson.station.low": "Hodge III+ · engaged (m = 11)",
    "johnson.result.efw": "Estimated fetal weight",
    "johnson.note":
      "Formula assumes vertex presentation. Less reliable in polyhydramnios, oligohydramnios, multiple gestation, or BMI >30.",
    "johnson.empty": "Enter fundal height to estimate fetal weight.",

    // Baxter
    "baxter.title": "Baxter (Parkland) Burn Resuscitation",
    "baxter.subtitle":
      "First-24-hour fluid resuscitation for thermal burns ≥ 20 % TBSA (adult) or ≥ 10 % (pediatric). Crystalloid (lactated Ringer's preferred). Titrate to urine output 0.5 ml/kg/h adult, 1 ml/kg/h pediatric.",
    "baxter.formula": "Total fluid (ml) = 4 × weight (kg) × TBSA (%)   ·   50 % over first 8 h, 50 % over remaining 16 h",
    "baxter.weight": "Patient weight",
    "baxter.tbsa": "Total Body Surface Area burned",
    "baxter.tbsa.hint": "Partial- and full-thickness burns only. Use Rule of Nines or Lund-Browder.",
    "baxter.factor": "IV tubing drop factor",
    "baxter.factor.macro": "Macro · 20 gtt/ml",
    "baxter.factor.micro": "Micro · 60 gtt/ml",
    "baxter.note":
      "Time zero = time of burn injury, NOT time of presentation. Subtract any fluid already given. Crystalloid only — colloids withheld in the first 24 h.",
    "baxter.result.total": "Total fluid (24 h)",
    "baxter.block.first8": "First 8 hours (50 %)",
    "baxter.block.last16": "Remaining 16 hours (50 %)",
    "baxter.rate.ml": "Infusion rate",
    "baxter.rate.gtt": "Drip rate",
    "baxter.empty": "Enter weight and TBSA to compute the resuscitation plan.",
    "nav.baxter": "Baxter Resuscitation",
    "nav.baxter.sub": "Burn fluid (Parkland)",

    // IV Fluid drip rates
    "iv.macro": "Macroset drip rate (20 gtt/ml)",
    "iv.micro": "Microset drip rate (60 mgtt/ml)",

    // Emergency Drugs
    "emerg.title": "Emergency Drug Infusions",
    "emerg.subtitle":
      "ACLS / critical-care infusion calculator. Final concentration and syringe-pump rate based on drug strength, syringe volume and target dose. Always verify with current ACLS guidelines.",
    "emerg.formula_lead": "Standard formula",
    "emerg.drug": "Drug",
    "emerg.weight": "Patient weight",
    "emerg.strength": "Drug strength (sediaan)",
    "emerg.volume": "Syringe / diluent volume",
    "emerg.target_dose": "Target dose",
    "emerg.typical_range": "Typical range",
    "emerg.result.pump_rate": "Syringe pump rate",
    "emerg.result.conc_mcg": "Final concentration",
    "emerg.result.conc_mg": "Final concentration",
    "emerg.empty": "Enter weight and dose.",
    "emerg.diluent": "Diluent",
    "emerg.dose_unit": "Dose unit",

    // Heparin
    "hep.title": "Heparin Protocol",
    "hep.subtitle":
      "Weight-based unfractionated heparin: initial bolus and maintenance infusion using standard 25 000 U / 50 cc syringe. Indication-specific dosing with hard maximum caps.",
    "hep.formula":
      "Bolus = U/kg × weight (capped)   ·   Maintenance = U/kg/h × weight ÷ concentration (capped)",
    "hep.indication": "Indication",
    "hep.indication.acs": "Acute Coronary Syndrome (ACS)",
    "hep.indication.vte": "VTE (DVT / Pulmonary Embolism)",
    "hep.weight": "Patient weight",
    "hep.total_units": "Total units (sediaan)",
    "hep.total_volume": "Total volume",
    "hep.bolus_target": "Bolus target",
    "hep.bolus_hint": "Default {def} U/kg · max {max} U total.",
    "hep.maint_target": "Maintenance target",
    "hep.maint_hint": "Default {def} U/kg/h · max {max} U/hr. Adjust against aPTT 1.5–2.5× control.",
    "hep.note":
      "Check baseline aPTT, PT/INR, platelets, creatinine. Recheck aPTT 6 h after start and after every dose change.",
    "hep.cap_badge": "Max reached",
    "hep.cap_warning":
      "Calculated {raw} U exceeds the hard maximum ({max}). Output clamped to cap.",
    "hep.result.bolus_units": "Initial bolus",
    "hep.result.bolus_cc": "Bolus volume to push",
    "hep.result.maint_cc": "Maintenance infusion",
    "hep.result.maint_units": "= units / hr  &  mass equivalent",
    "hep.empty": "Enter weight to compute bolus and infusion rate.",
  },

  id: {
    // Brand & footer
    "brand.subtitle": "Hitung Mendadak Malam-Malam",
    "sidebar.lang_label": "Bahasa",
    "sidebar.footer.version": "v0.1 · Untuk penggunaan profesional.",
    "sidebar.footer.disclaimer":
      "Selalu verifikasi dengan protokol lokal dan apoteker.",

    // Nav
    "nav.iv_fluid": "Cairan Rumatan IV",
    "nav.iv_fluid.sub": "Holliday-Segar",
    "nav.pediatric": "Dosis Pediatri",
    "nav.pediatric.sub": "Berat badan mg/kg",
    "nav.drip": "Tetesan Infus",
    "nav.drip.sub": "Tetes / menit",
    "nav.electrolytes": "Elektrolit",
    "nav.electrolytes.sub": "Koreksi Na & K",
    "nav.nihss": "NIHSS",
    "nav.nihss.sub": "Skala stroke",
    "nav.alvarado": "Skor Alvarado",
    "nav.alvarado.sub": "Apendisitis",
    "nav.bmi": "IMT (BMI)",
    "nav.bmi.sub": "Indeks Massa Tubuh",
    "nav.obgyn": "OB-Gyn",
    "nav.obgyn.sub": "HPL & Usia Kehamilan",
    "nav.group.critical": "Gawat & Kritis",
    "nav.group.scores": "Skor & Spesialitas",
    "nav.group.neonatology": "Neonatologi",
    "nav.group.others": "Lainnya",
    "nav.emergency": "Obat Emergensi",
    "nav.emergency.sub": "Infus ACLS",
    "nav.heparin": "Protokol Heparin",
    "nav.heparin.sub": "Bolus & rumatan",
    "nav.dev_prefix": "dalam pengembangan · ",

    // Top-right widget
    "widget.label": "Masukan & Dukungan",
    "widget.title": "Bantu kembangkan HMMM",
    "widget.subtitle": "Kirim masukan atau dukung pengembangan.",
    "widget.feedback.title": "Masukan",
    "widget.feedback.body":
      "Laporkan bug, minta kalkulator baru, atau bagikan catatan klinis.",
    "widget.feedback.cta": "Buka Google Form",
    "widget.donate.title": "Donasi",
    "widget.donate.body":
      "Pindai QR dengan kamera Anda untuk mendukung pengembangan.",
    "widget.thanks": "Terima kasih telah menjaga aplikasi ini tetap gratis & terbuka.",

    // Changelog
    "changelog.kicker": "Catatan rilis",
    "changelog.title": "Yang baru",
    "changelog.current": "Sekarang",
    "gemini.title": "Tanya Gemini",
    "gemini.body": "Bantuan klinis berbasis AI akan hadir di v2.1.",
    "gemini.placeholder": "Tanya pertanyaan klinis — mis. ‘dosis parasetamol anak 16 kg’ atau ‘interpretasi NIHSS 12’.",
    "gemini.ask": "Tanya",
    "gemini.thinking": "Memproses…",
    "gemini.disclaimer":
      "Jawaban AI dapat tidak akurat. Selalu verifikasi dengan protokol lokal, apoteker, dan status ginjal/hepar pasien.",

    // Theme
    "theme.label": "Tema",
    "theme.light": "Terang",
    "theme.dark": "Gelap",

    // Generic
    "common.calculator": "Kalkulator",
    "common.section": "Bagian",
    "common.reset": "Atur ulang",
    "common.reset_all": "Atur ulang semua",
    "common.dev_badge": "Dev",
    "common.in_development": "dalam pengembangan",
    "common.in_development.long": "Dalam pengembangan.",
    "common.no_data_stored": "Data tidak disimpan",
    "footer.tagline": "Gratis • Ringan • Tanpa Login • Bebas Iklan • 100% Privat",
    "footer.privacy":
      "🔒 Privacy Guaranteed: Aplikasi ini tidak mengumpulkan, menyimpan, atau merekam data klinis maupun identitas pasien Anda. Semua perhitungan berjalan secara lokal di perangkat Anda.",

    // Home
    "home.kicker": "HMMM · Hitung Mendadak Malam-Malam",
    "home.headline.1": "Hitungannya, beres.",
    "home.headline.2": "Anda fokus ke pasien.",
    "home.lead":
      "Pendamping praktis di samping tempat tidur untuk IGD dan bangsal — sepuluh kalkulator tanpa basa-basi yang memperbarui hasil saat Anda mengetik dan menampilkan setiap langkah perhitungan. Tanpa login, tanpa pelacakan, tanpa menunggu.",
    "home.calculators": "Kalkulator",
    "home.disclaimer.label": "Disclaimer.",
    "home.disclaimer.body":
      "Alat ini membantu, bukan memutuskan. Verifikasi setiap dosis dengan formularium lokal terbaru dan fungsi ginjal/hati pasien sebelum pemberian.",
    "home.card.iv_fluid.sub": "Holliday-Segar (aturan 4-2-1)",
    "home.card.iv_fluid.desc":
      "Kebutuhan cairan rumatan per jam dan per 24 jam berdasarkan berat badan.",
    "home.card.pediatric.sub": "Berat badan mg/kg",
    "home.card.pediatric.desc":
      "Obat-obat IGD umum (parasetamol, amoksisilin, seftriakson, deksametason, adrenalin, …).",
    "home.card.drip.sub": "Tetes / menit dari volume & waktu",
    "home.card.drip.desc":
      "Tetes per menit untuk infus gravitasi (10, 15, 20, 60 gtt/ml).",
    "home.card.electrolytes.sub": "Defisit Na & koreksi K",
    "home.card.electrolytes.desc":
      "Estimasi defisit natrium, defisit air bebas, dan koreksi kalium.",
    "home.card.nihss.sub": "Skala stroke (0–42)",
    "home.card.nihss.desc":
      "Skala Stroke NIH dengan 15 item dan stratifikasi keparahan.",
    "home.card.alvarado.sub": "Apendisitis (MANTRELS)",
    "home.card.alvarado.desc":
      "Probabilitas apendisitis akut dari gejala, tanda, dan laboratorium.",

    // Home tabs (v2.1)
    "tabs.algorithm": "Medical Algorithm",
    "tabs.drug": "Drug & Fluid",
    "tabs.guidelines": "Guidelines",
    "tabs.scores": "Clinical Scores",
    "scores.intro":
      "Sistem skor samping tempat tidur standar — triase cepat, stratifikasi risiko, dan putuskan eskalasi.",
    "score.killip.title": "Klasifikasi Killip",
    "score.killip.desc": "Stratifikasi risiko mortalitas 30 hari pasca-IMA.",
    "score.sofa.title": "SOFA / qSOFA",
    "score.sofa.desc": "Skrining sepsis (qSOFA) + skor disfungsi organ (SOFA).",
    "score.news.title": "NEWS2",
    "score.news.desc": "Skor peringatan dini nasional — 7 parameter vital.",
    "score.apgar.title": "Skor Apgar",
    "score.apgar.desc": "Penilaian bayi baru lahir 5 kriteria pada menit 1 dan 5.",
    "score.ballard.title": "New Ballard Score",
    "score.ballard.desc": "Usia gestasi dari 12 kriteria pemeriksaan fisik & neuromuskular.",
    "score.downe.title": "Skor Downe",
    "score.downe.desc": "Skrining distres napas neonatal — 5 kriteria, ambang PONED.",
    "home.collab.prefix": "Berkolaborasi dengan ",
    "home.collab.suffix": " untuk algoritma & pedoman medis.",
    "home.collab2.prefix": "Berkolaborasi dengan ",
    "home.collab2.suffix": " untuk seri kasus medis dan e-book. Kunjungi akunnya!",
    "drug.intro":
      "Pintasan langsung ke kalkulator dosis obat dan cairan yang sudah tersedia di aplikasi ini.",
    "guidelines.intro":
      "Ringkasan ACLS, ATLS, WHO IMCI, dan formularium lokal akan hadir di v2.2. Sementara itu setiap halaman kalkulator sudah mencantumkan formula rujukan.",
    "common.coming_v22": "Hadir di v2.2",

    // Medical Algorithm — specialty index (di tab Home)
    "algo.intro":
      "Algoritma penyakit & kegawatdaruratan, dikelompokkan per spesialisasi. Pilih section untuk melihat daftar algoritma.",
    "algo.count": "{n} algoritma tersedia",
    "algo.spec.cardiovascular": "Cardiovascular",
    "algo.spec.respiratory": "Respiratory",
    "algo.spec.neurology": "Neurology",
    "algo.spec.pediatric": "Pediatric",
    "algo.spec.anestesi": "Anestesi & Airway",
    "algo.footer_note":
      "Flowchart interaktif untuk spesialisasi lain akan hadir di v2.2. Alur Cardiovascular (ACLS, STEMI) sudah aktif.",

    // Halaman algoritma (indeks Cardiovascular + flowchart)
    "algo.cardiovascular.title": "Algoritma Cardiovascular",
    "algo.cardiovascular.description":
      "Alur interaktif langkah demi langkah untuk kegawatdaruratan kardiovaskular yang paling time-critical.",
    "algo.list.intro": "Daftar algoritma pada section ini. Klik yang berstatus \"Tersedia\" untuk membuka.",
    "algo.badge.ready": "Tersedia",
    "algo.badge.soon": "Segera hadir",
    "algo.back_home": "Kembali ke Beranda",
    "algo.back_to_list": "Kembali ke Cardiovascular",
    "algo.back_to_specialty": "Kembali ke {name}",
    "algo.step_of": "Langkah {i} dari {n}",
    "algo.previous": "Sebelumnya",
    "algo.restart": "Mulai ulang",
    "algo.disclaimer.label": "Sebagai rujukan saja.",
    "algo.disclaimer.body":
      "Flowchart ini merangkum pedoman terpublikasi dan hanya alat bantu ingatan di samping tempat tidur. Selalu ikuti protokol lokal dan verifikasi dosis dengan apoteker Anda.",

    // IV Fluid
    "iv.title": "Cairan Rumatan IV",
    "iv.subtitle":
      "Metode Holliday-Segar (4-2-1) untuk kebutuhan cairan rumatan harian dan per jam pada pasien anak dan dewasa.",
    "iv.formula":
      "4 ml/kg/jam (10 kg pertama) + 2 ml/kg/jam (10 kg berikutnya) + 1 ml/kg/jam (setiap kg > 20)",
    "iv.weight": "Berat badan pasien",
    "iv.weight.placeholder": "mis. 18",
    "iv.note":
      "Gunakan berat badan aktual pada pasien non-obesitas. Pada dewasa obesitas pertimbangkan berat badan ideal atau batasi laju cairan sesuai kebijakan lokal.",
    "iv.result.rate": "Laju rumatan",
    "iv.result.day": "Total 24 jam",
    "iv.breakdown.empty":
      "Masukkan berat badan untuk melihat rincian perhitungan.",

    // Pediatric
    "ped.title": "Dosis Obat Pediatri",
    "ped.subtitle":
      "Dosis berdasarkan berat badan (mg/kg) untuk obat-obat IGD umum. Dosis maksimum tunggal dan harian diterapkan otomatis.",
    "ped.formula":
      "Dosis (mg) = berat (kg) × mg/kg     —     dibatasi dosis tunggal maks",
    "ped.medication": "Obat",
    "ped.weight": "Berat badan pasien",
    "ped.weight.placeholder": "mis. 14",
    "ped.custom": "Dosis per kg (default {default} mg/kg)",
    "ped.custom.hint": "Rentang: {min}–{max} mg/kg · Frekuensi: {freq}",
    "ped.dev_suffix": " · (dalam pengembangan)",
    "ped.capped":
      "Dibatasi dosis tunggal maks. Dosis hasil hitung ({raw} mg) melebihi maksimum ({max} mg).",
    "ped.dev_alert":
      "Nilai dosis untuk obat ini belum ditinjau secara klinis. Verifikasi dengan formularium lokal sebelum digunakan.",
    "ped.result.single": "Dosis per kali pemberian",
    "ped.result.volume": "Volume yang ditarik",
    "ped.result.frequency": "Frekuensi",
    "ped.result.daily_max": "Maksimum harian",
    "ped.breakdown.empty": "Pilih obat, masukkan berat badan.",
    "ped.breakdown.route": "Rute",

    // Drip
    "drip.title": "Tetesan Infus IV",
    "drip.subtitle":
      "Kalkulator infus gravitasi. Menghitung tetes per menit dan ml per jam dari volume total, durasi, dan faktor tetes set infus Anda.",
    "drip.formula": "gtt/menit = (volume × faktor tetes) ÷ waktu (menit)",
    "drip.volume": "Volume total",
    "drip.volume.placeholder": "mis. 1000",
    "drip.hours": "Jam",
    "drip.hours.placeholder": "mis. 8",
    "drip.minutes": "Menit",
    "drip.minutes.placeholder": "0",
    "drip.factor": "Faktor tetes (set infus)",
    "drip.factor.macro10": "Makro · 10 gtt/ml",
    "drip.factor.macro15": "Makro · 15 gtt/ml",
    "drip.factor.macro20": "Makro · 20 gtt/ml",
    "drip.factor.micro60": "Mikro · 60 gtt/ml",
    "drip.note":
      "Faktor tetes makroset tercetak pada bilik infus. Mikroset (60 gtt/ml) untuk infus volume kecil yang presisi, terutama pada pediatri.",
    "drip.result.gtt": "Laju tetesan",
    "drip.result.ml": "Laju aliran",
    "drip.breakdown.empty": "Masukkan volume, waktu, dan faktor tetes.",

    // Electrolytes
    "elec.title": "Koreksi Elektrolit",
    "elec.subtitle":
      "Estimasi defisit natrium / defisit air bebas dan koreksi kalium. Koreksi selalu perlahan dan periksa ulang laboratorium.",
    "elec.formula":
      "Defisit Na (mEq) = TBW × (Na target − Na terukur)   ·   FWD (L) = TBW × (Na/140 − 1)",
    "elec.dev_banner":
      "Formula (TBW, defisit Na, FWD, defisit K empiris) belum ditinjau secara klinis. Gunakan sebagai perkiraan dan verifikasi dengan protokol lokal sebelum meresepkan.",
    "elec.weight": "Berat badan pasien",
    "elec.weight.placeholder": "mis. 70",
    "elec.tbw": "Estimasi TBW",
    "elec.tbw.male": "Pria dewasa — 0.6 × kg",
    "elec.tbw.female": "Wanita dewasa — 0.5 × kg",
    "elec.tbw.geriatric_male": "Pria geriatri — 0.5 × kg",
    "elec.tbw.geriatric_female": "Wanita geriatri — 0.45 × kg",
    "elec.tbw.child": "Anak — 0.6 × kg",
    "elec.tbw.infant": "Bayi — 0.7 × kg",
    "elec.sodium": "Koreksi natrium",
    "elec.na.measured": "Na terukur",
    "elec.na.desired": "Na target",
    "elec.na.note":
      "Koreksi hiponatremia kronik maksimal 8-10 mEq/L per 24 jam untuk mencegah Osmotic Demyelination Syndrome (ODS). Pertimbangkan memasukkan angka Desired Na sebesar [Measured Na + 8] untuk panduan 24 jam pertama.",
    "elec.result.na_deficit": "Defisit natrium",
    "elec.result.fwd": "Defisit air bebas",
    "elec.result.tbw": "Total cairan tubuh",
    "elec.result.correction_time": "Waktu koreksi min. (8 mEq/L/24j)",
    "elec.breakdown.empty": "Masukkan berat dan nilai natrium.",
    "elec.potassium": "Koreksi kalium",
    "elec.k.measured": "K⁺ terukur",
    "elec.k.note":
      "Koreksi > 10 mEq/jam (jalur sentral) WAJIB disertai monitoring EKG secara kontinu.",
    "elec.result.k_deficit": "Estimasi defisit K",
    "elec.result.k_peripheral": "Waktu infus (perifer · 10 mEq/jam)",
    "elec.result.k_central": "Waktu infus (sentral · 20 mEq/jam)",
    "elec.k.breakdown.empty": "Masukkan berat dan nilai kalium.",

    // NIHSS
    "nihss.title": "NIHSS — Skala Stroke",
    "nihss.subtitle":
      "National Institutes of Health Stroke Scale. 15 item, 0–42 poin. Dokumentasikan saat presentasi, pasca-tPA, dan pada 24 jam.",
    "nihss.formula": "Total = jumlah semua item (0 sampai 42)",
    "nihss.result.total": "Total NIHSS",
    "nihss.result.unit": "/ 42 · {answered}/{total} terisi",
    "nihss.result.severity": "Keparahan",
    "nihss.severity.none": "Tidak ada gejala stroke",
    "nihss.severity.minor": "Stroke ringan",
    "nihss.severity.moderate": "Stroke sedang",
    "nihss.severity.moderate_severe": "Stroke sedang–berat",
    "nihss.severity.severe": "Stroke berat",
    "nihss.legend":
      "0           Tidak ada gejala stroke\n1–4        Stroke ringan\n5–15       Stroke sedang\n16–20      Stroke sedang–berat\n21–42      Stroke berat",
    "nihss.note":
      "Gunakan total NIHSS bersama penilaian klinis, waktu onset, dan pencitraan saat mempertimbangkan trombolisis atau trombektomi.",

    // Alvarado
    "alv.title": "Skor Alvarado — Apendisitis",
    "alv.subtitle":
      "Kriteria MANTRELS untuk suspek apendisitis akut pada dewasa. Skor 0–10 menstratifikasi probabilitas dan memandu pencitraan vs rujukan bedah.",
    "alv.formula":
      "Σ poin dari 8 kriteria (maks 10). Nyeri tekan RLQ dan leukositosis bobot ×2.",
    "alv.group.symptoms": "Gejala",
    "alv.group.signs": "Tanda",
    "alv.group.labs": "Laboratorium",
    "alv.result.total": "Skor total",
    "alv.result.interpretation": "Interpretasi",
    "alv.interp.low": "Pikirkan kemungkinan lain",
    "alv.interp.possible": "Mungkin apendisitis — observasi / pencitraan",
    "alv.interp.probable": "Kemungkinan besar apendisitis — konsul bedah",
    "alv.interp.very":
      "Sangat mungkin apendisitis — rujuk bedah segera",
    "alv.legend":
      "0–3   Pikirkan kemungkinan lain  — pulang aman\n4–6   Mungkin              — observasi / pencitraan (CT, US)\n7–8   Kemungkinan besar   — konsul bedah\n9–10  Sangat mungkin       — rujuk bedah segera",
    "alv.note":
      "Alvarado kurang reliabel pada anak, kehamilan, dan lansia. Kombinasikan dengan penilaian klinis dan pencitraan bila diindikasikan.",

    // BMI
    "bmi.title": "Indeks Massa Tubuh (IMT)",
    "bmi.subtitle":
      "IMT dewasa dengan dua skala referensi — Internasional (WHO) dan Asia-Pasifik (WHO 2004 / IDF). Beralih antara keduanya untuk melihat perubahan kategori.",
    "bmi.formula": "IMT = berat (kg) ÷ tinggi (m)²",
    "bmi.weight": "Berat badan",
    "bmi.weight.placeholder": "mis. 65",
    "bmi.height": "Tinggi badan",
    "bmi.height.placeholder": "mis. 170",
    "bmi.criteria": "Skala referensi",
    "bmi.criteria.international": "Internasional (WHO)",
    "bmi.criteria.asia_pacific": "Asia-Pasifik (WHO 2004)",
    "bmi.result.bmi": "IMT",
    "bmi.result.category": "Kategori",
    "bmi.cat.underweight": "Berat kurang",
    "bmi.cat.normal": "Normal",
    "bmi.cat.overweight": "Berat lebih",
    "bmi.cat.at_risk": "Berat lebih (berisiko)",
    "bmi.cat.obese1": "Obesitas kelas I",
    "bmi.cat.obese2": "Obesitas kelas II",
    "bmi.cat.obese3": "Obesitas kelas III",
    "bmi.legend.international":
      "< 18.5    Berat kurang\n18.5–24.9 Normal\n25.0–29.9 Berat lebih\n30.0–34.9 Obesitas kelas I\n35.0–39.9 Obesitas kelas II\n≥ 40.0    Obesitas kelas III",
    "bmi.legend.asia_pacific":
      "< 18.5    Berat kurang\n18.5–22.9 Normal\n23.0–24.9 Berat lebih (berisiko)\n25.0–29.9 Obesitas kelas I\n≥ 30.0    Obesitas kelas II",
    "bmi.note":
      "IMT tidak membedakan otot dan lemak. Pada atlet, kehamilan, dan edema, interpretasikan dengan hati-hati. Cut-off Asia-Pasifik (lebih rendah) banyak digunakan untuk populasi Asia Tenggara.",
    "bmi.empty": "Masukkan berat dan tinggi.",

    // OB-Gyn
    "obgyn.title": "OB-Gyn — HPL & Usia Kehamilan",
    "obgyn.subtitle":
      "Estimasi Hari Perkiraan Lahir (rumus Naegele) dan Usia Kehamilan saat ini dari Hari Pertama Haid Terakhir (HPHT).",
    "obgyn.formula":
      "HPL = HPHT + 7 hari − 3 bulan + 1 tahun   ·   UK = hari ini − HPHT",
    "obgyn.lmp": "HPHT (Hari Pertama Haid Terakhir)",
    "obgyn.lmp.hint": "Hari pertama haid terakhir normal.",
    "obgyn.today": "Hari ini",
    "obgyn.result.edd": "Hari Perkiraan Lahir",
    "obgyn.result.ga": "Usia Kehamilan",
    "obgyn.result.ga.unit": "minggu · hari",
    "obgyn.result.days_remaining": "Sisa hari",
    "obgyn.result.days_remaining.unit": "hari",
    "obgyn.result.trimester": "Trimester",
    "obgyn.trimester.1": "Pertama (≤ 13⁺⁶)",
    "obgyn.trimester.2": "Kedua (14⁰ – 27⁺⁶)",
    "obgyn.trimester.3": "Ketiga (≥ 28⁰)",
    "obgyn.result.past_due": "Lewat HPL",
    "obgyn.weeks": "minggu",
    "obgyn.days": "hari",
    "obgyn.note":
      "Rumus Naegele mengasumsikan siklus 28 hari yang teratur. Pada siklus tidak teratur atau pasca-IVF, prioritaskan USG trimester pertama (CRL).",
    "obgyn.empty": "Pilih tanggal HPHT.",
    "obgyn.future_lmp": "HPHT tidak boleh di masa depan.",
    "obgyn.too_far":
      "HPHT tampak lebih dari 45 minggu lalu — mohon verifikasi tanggal.",
    "obgyn.today.hint":
      "Default hari ini. Backdate untuk verifikasi catatan ANC sebelumnya.",

    // Johnson-Toshack
    "johnson.title": "Taksiran Berat Janin (Johnson-Toshack)",
    "johnson.subtitle":
      "Estimasi berat janin di samping tempat tidur dari tinggi fundus uteri dan stasiun bagian presentasi.",
    "johnson.tfu": "Tinggi Fundus Uteri (TFU)",
    "johnson.station": "Stasiun / Konvergensi",
    "johnson.station.high": "Symphysis · Belum Masuk PAP (m = 12)",
    "johnson.station.low": "Hodge III+ · Sudah Masuk PAP (m = 11)",
    "johnson.result.efw": "Taksiran berat janin",
    "johnson.note":
      "Formula mengasumsikan presentasi vertex. Kurang reliabel pada polihidramnion, oligohidramnion, gemelli, atau IMT > 30.",
    "johnson.empty": "Masukkan TFU untuk estimasi berat janin.",

    // Baxter
    "baxter.title": "Resusitasi Baxter (Luka Bakar)",
    "baxter.subtitle":
      "Resusitasi cairan 24 jam pertama untuk luka bakar termal ≥ 20 % TBSA (dewasa) atau ≥ 10 % (anak). Kristaloid (RL diutamakan). Titrasi terhadap produksi urin 0.5 ml/kg/jam dewasa, 1 ml/kg/jam anak.",
    "baxter.formula": "Total cairan (ml) = 4 × berat (kg) × TBSA (%)   ·   50 % dalam 8 jam pertama, 50 % dalam 16 jam berikutnya",
    "baxter.weight": "Berat badan pasien",
    "baxter.tbsa": "Luas Luka Bakar (TBSA)",
    "baxter.tbsa.hint": "Hanya luka bakar derajat II–III. Gunakan Rule of Nines atau Lund-Browder.",
    "baxter.factor": "Faktor tetes infus",
    "baxter.factor.macro": "Makro · 20 gtt/ml",
    "baxter.factor.micro": "Mikro · 60 gtt/ml",
    "baxter.note":
      "Waktu nol = saat cedera bakar, BUKAN saat presentasi. Kurangi cairan yang sudah diberikan sebelumnya. Hanya kristaloid — koloid ditahan dalam 24 jam pertama.",
    "baxter.result.total": "Total cairan (24 jam)",
    "baxter.block.first8": "8 jam pertama (50 %)",
    "baxter.block.last16": "16 jam berikutnya (50 %)",
    "baxter.rate.ml": "Laju infus",
    "baxter.rate.gtt": "Laju tetesan",
    "baxter.empty": "Masukkan berat dan TBSA untuk menghitung rencana resusitasi.",
    "nav.baxter": "Resusitasi Baxter",
    "nav.baxter.sub": "Cairan luka bakar (Parkland)",

    // IV Fluid drip rates
    "iv.macro": "Laju makroset (20 gtt/ml)",
    "iv.micro": "Laju mikroset (60 mgtt/ml)",

    // Emergency Drugs
    "emerg.title": "Infus Obat Emergensi",
    "emerg.subtitle":
      "Kalkulator infus ACLS / perawatan kritis. Konsentrasi akhir dan laju syringe pump berdasarkan sediaan, volume spuit, dan dosis target. Verifikasi selalu dengan pedoman ACLS terkini.",
    "emerg.formula_lead": "Formula standar",
    "emerg.drug": "Obat",
    "emerg.weight": "Berat badan pasien",
    "emerg.strength": "Sediaan obat (mg)",
    "emerg.volume": "Volume spuit / pengencer",
    "emerg.target_dose": "Dosis target",
    "emerg.typical_range": "Rentang umum",
    "emerg.result.pump_rate": "Laju syringe pump",
    "emerg.result.conc_mcg": "Konsentrasi akhir",
    "emerg.result.conc_mg": "Konsentrasi akhir",
    "emerg.empty": "Masukkan berat dan dosis.",
    "emerg.diluent": "Pengencer",
    "emerg.dose_unit": "Satuan dosis",

    // Heparin
    "hep.title": "Protokol Heparin",
    "hep.subtitle":
      "Heparin tidak terfraksinasi berbasis berat badan: bolus awal dan infus rumatan dengan sediaan standar 25 000 U / 50 cc. Dosis berbeda per indikasi dengan batas maksimum keras.",
    "hep.formula":
      "Bolus = U/kg × berat (dibatasi)   ·   Rumatan = U/kg/jam × berat ÷ konsentrasi (dibatasi)",
    "hep.indication": "Indikasi",
    "hep.indication.acs": "Sindrom Koroner Akut (ACS)",
    "hep.indication.vte": "VTE (DVT / Emboli Paru)",
    "hep.weight": "Berat badan pasien",
    "hep.total_units": "Total unit (sediaan)",
    "hep.total_volume": "Volume total",
    "hep.bolus_target": "Target bolus",
    "hep.bolus_hint": "Default {def} U/kg · maks {max} U total.",
    "hep.maint_target": "Target rumatan",
    "hep.maint_hint": "Default {def} U/kg/jam · maks {max} U/jam. Sesuaikan terhadap aPTT 1.5–2.5× kontrol.",
    "hep.note":
      "Periksa aPTT baseline, PT/INR, trombosit, kreatinin. Periksa ulang aPTT 6 jam setelah inisiasi dan setiap perubahan dosis.",
    "hep.cap_badge": "Batas tercapai",
    "hep.cap_warning":
      "Hasil {raw} U melebihi batas maksimum ({max}). Hasil dibatasi pada cap.",
    "hep.result.bolus_units": "Bolus awal",
    "hep.result.bolus_cc": "Volume bolus untuk push",
    "hep.result.maint_cc": "Infus rumatan",
    "hep.result.maint_units": "= unit / jam & ekuivalen massa",
    "hep.empty": "Masukkan berat untuk menghitung bolus dan laju infus.",
  },
};

export function translate(lang, key, vars) {
  const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
  let str = dict[key];
  if (str === undefined) str = TRANSLATIONS.en[key] ?? key;
  if (vars) {
    for (const k of Object.keys(vars)) {
      str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(vars[k]));
    }
  }
  return str;
}
